// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from "@stencil/core";
import { Component, Element, h, Host, Prop, State } from "@stencil/core";

import { triggerSlotChange } from "../utils";

/**
 * @slot subscription - Subscription form or content for the footer.
 * @slot cta - Call-to-action content for the footer.
 * @slot methods - Payment or method icons for the footer.
 * @slot media - Media logos for the footer.
 * @slot links - Bottom links for the footer.
 */
@Component({
  tag: "qds-web-footer",
  shadow: true,
  styleUrl: "footer.css",
})
export class Footer implements ComponentInterface {
  /**
   * The year of the copyright.
   *
   * @public
   * @type {number}
   */
  @Prop() public copyrightYear: number;

  /**
   * Optional link for the copyright owner.
   *
   * @public
   * @type {string}
   */
  @Prop() public hrefCopyright?: string;

  /**
   * The href for the company Logo.
   *
   * @remarks
   * This property is used to specify the URL for the web logo.
   *
   * @public
   * @type {string}
   */
  @Prop() public hrefLogo: string;

  /**
   * The src for the company Logo.
   *
   * @remarks
   * This property is used to specify the src for the web company logo.
   *
   * @public
   * @type {string}
   */
  @Prop() public srcLogo: string;

  @Element() public el: HTMLElement;

  @State() private hideCtaSlot = false;

  @State() private hideDefaultSlot = false;

  @State() private hideMethodsSlot = false;

  public componentDidLoad(): void {
    const { shadowRoot } = this.el;
    if (!shadowRoot) return;

    triggerSlotChange(shadowRoot, this.handleSlotChange);
  }

  public render() {
    const isSmallHeight =
      this.hideMethodsSlot && this.hideCtaSlot && this.hideDefaultSlot;

    return (
      <Host class="qds-theme-inverted">
        <footer class={{ "small-height": isSmallHeight }}>
          <img
            loading="lazy"
            class="image"
            src="https://d2osz8slymlqdp.cloudfront.net/@quartzds/se-assets/@1.0.0-beta.2/illustrations/illustration-footer.svg"
            alt=""
            aria-hidden="true"
          />
          <div class="content">
            <slot name="subscription" />
            <div class={{ cta: true, hidden: this.hideCtaSlot }}>
              <slot name="cta" onSlotchange={this.handleSlotChange} />
              <qds-divider />
            </div>
            <div class={{ menu: true, hidden: this.hideDefaultSlot }}>
              <slot onSlotchange={this.handleSlotChange} />
            </div>
            <div class={{ methods: true, hidden: this.hideMethodsSlot }}>
              <slot name="methods" onSlotchange={this.handleSlotChange} />
            </div>
            <div class="media-logo">
              <div>
                <slot name="media" />
              </div>
              <a
                class="logo"
                href={this.hrefLogo}
                target="_blank"
                rel="noreferrer"
              >
                <img class="logo" src={this.srcLogo} alt="Impact company" />
              </a>
            </div>
            <div class="bottom-links">
              <slot name="links" />
              <p class="copyright">
                {(this.hrefCopyright ?? "").trim() === "" ? (
                  <span>
                    © {this.copyrightYear}{" "}
                    <span lang="en">Schneider Electric</span>
                  </span>
                ) : (
                  <a
                    href={this.hrefCopyright}
                    target="_blank"
                    rel="noopener noreferrer"
                    lang="en"
                  >
                    © {this.copyrightYear}{" "}
                    <span lang="en">Schneider Electric</span>
                  </a>
                )}
              </p>
            </div>
          </div>
        </footer>
      </Host>
    );
  }

  private readonly handleSlotChange = (event: Event): void => {
    const slot = event.target as HTMLSlotElement;
    const isEmpty = slot.assignedElements().length === 0;

    if (!slot.name) {
      this.hideDefaultSlot = isEmpty;
    }

    switch (slot.name) {
      case "methods": {
        this.hideMethodsSlot = isEmpty;
        break;
      }
      case "cta": {
        this.hideCtaSlot = isEmpty;
        break;
      }
      default: {
        break;
      }
    }
  };
}
